import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  LogOut, 
  User, 
  FileText, 
  Calendar, 
  BookOpen, 
  MessageSquare,
  ChevronRight,
  Download
} from 'lucide-react';

// Login Component
const Login = () => {
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <main className="pt-20 min-h-screen bg-brand-charcoal flex items-center justify-center" data-testid="portal-login">
      <div className="max-w-md w-full mx-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-8 shadow-2xl"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/assets/images/logo-purple.png"
                alt="Expertize Edge"
                className="h-12 w-12 mx-auto"
              />
            </Link>
            <h1 className="font-heading text-3xl font-bold text-brand-charcoal mb-2">
              Client Portal
            </h1>
            <p className="text-brand-muted">
              Sign in to access your resources
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-brand-body mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-brand-lavender rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
                data-testid="login-email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-brand-body mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-brand-lavender rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-gold text-brand-charcoal font-semibold py-4 hover:bg-brand-gold-lite transition-all duration-300 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/forgot-password" className="text-brand-purple text-sm hover:underline">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-brand-lavender text-center">
            <p className="text-brand-muted text-sm">
              Don't have an account?{' '}
              <Link to="/contact" className="text-brand-purple hover:underline">
                Contact us
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    // In a real app, fetch dashboard data from API
    setDashboardData({
      program: {
        name: 'Executive Leadership Program',
        progress: 65,
        nextSession: '2025-01-20',
      },
      resources: [
        { id: '1', title: 'Leadership Framework Guide', category: 'Guide', date: '2025-01-10' },
        { id: '2', title: 'Session 3 Notes', category: 'Notes', date: '2025-01-08' },
        { id: '3', title: 'Decision Matrix Template', category: 'Template', date: '2025-01-05' },
      ],
      announcements: [
        { id: '1', title: 'New resources available', date: '2025-01-12' },
      ],
    });
  }, []);

  if (authLoading) {
    return (
      <main className="pt-20 min-h-screen bg-brand-bg-light flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-brand-purple border-t-transparent rounded-full"></div>
      </main>
    );
  }

  if (!user) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <main className="pt-20 min-h-screen bg-brand-bg-light" data-testid="portal-dashboard">
      {/* Header */}
      <div className="bg-brand-charcoal py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-brand-gold text-sm uppercase tracking-wider mb-2">
                Client Portal
              </p>
              <h1 className="font-heading text-3xl font-bold text-white">
                Welcome back, {user.name}
              </h1>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Program Overview */}
            {dashboardData?.program && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-8"
                data-testid="program-overview"
              >
                <div className="flex items-center gap-3 mb-6">
                  <BookOpen className="w-6 h-6 text-brand-purple" />
                  <h2 className="font-heading text-xl font-bold text-brand-charcoal">
                    Active Program
                  </h2>
                </div>
                <h3 className="text-2xl font-semibold text-brand-charcoal mb-4">
                  {dashboardData.program.name}
                </h3>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-brand-muted">Progress</span>
                    <span className="font-semibold text-brand-purple">
                      {dashboardData.program.progress}%
                    </span>
                  </div>
                  <div className="h-2 bg-brand-lavender rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-purple transition-all duration-500"
                      style={{ width: `${dashboardData.program.progress}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-brand-muted">
                  <Calendar className="w-4 h-4" />
                  <span>Next session: {new Date(dashboardData.program.nextSession).toLocaleDateString()}</span>
                </div>
              </motion.div>
            )}

            {/* Resources */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-8"
              data-testid="resources-section"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-brand-purple" />
                  <h2 className="font-heading text-xl font-bold text-brand-charcoal">
                    Recent Resources
                  </h2>
                </div>
                <Link to="/portal/resources" className="text-brand-purple text-sm hover:underline">
                  View All
                </Link>
              </div>
              <div className="space-y-4">
                {dashboardData?.resources.map((resource) => (
                  <div
                    key={resource.id}
                    className="flex items-center justify-between p-4 bg-brand-bg-light rounded-lg hover:bg-brand-lavender transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-brand-lavender rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-brand-purple" />
                      </div>
                      <div>
                        <p className="font-medium text-brand-charcoal">{resource.title}</p>
                        <p className="text-sm text-brand-muted">{resource.category}</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-brand-muted" />
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6"
              data-testid="quick-actions"
            >
              <h3 className="font-heading text-lg font-bold text-brand-charcoal mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Link
                  to="/contact"
                  className="flex items-center justify-between p-3 bg-brand-bg-light rounded-lg hover:bg-brand-lavender transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-brand-purple" />
                    <span className="text-brand-body">Contact Advisor</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-brand-muted" />
                </Link>
                <Link
                  to="/contact#booking"
                  className="flex items-center justify-between p-3 bg-brand-bg-light rounded-lg hover:bg-brand-lavender transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-brand-purple" />
                    <span className="text-brand-body">Book Session</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-brand-muted" />
                </Link>
              </div>
            </motion.div>

            {/* Profile Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-brand-charcoal rounded-xl p-6 text-white"
              data-testid="profile-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-brand-gold rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-brand-charcoal" />
                </div>
                <div>
                  <p className="font-semibold">{user.name}</p>
                  <p className="text-gray-400 text-sm">{user.email}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Member since {new Date(user.created_at).toLocaleDateString()}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
};

export { Login, Dashboard };
